/**
 * Rehype plugin that collapses runs of adjacent anchors into a single anchor
 * carrying the whole run, so the renderer can draw one citation chip with a
 * `+N` overflow instead of N separate links.
 *
 * Must run *after* Streamdown's `defaultRehypePlugins`. `rehype-sanitize` uses
 * the GitHub schema, whose allow-list for `<a>` is
 * `[aria*, dataFootnote*, className, href]` — any custom property written
 * before it is stripped. Running last means {@link CITE_GROUP_PROPERTY}
 * survives to the component.
 *
 * Deliberately free of React and of any citation-registry knowledge: the
 * plugin only marks up structure. Resolving hrefs to sources happens in the
 * leaf component via context, so a late-arriving registry can't be baked into
 * a stale plugin closure.
 *
 * Kept pure so it can be unit tested in isolation (see
 * rehype-group-citations.spec.ts).
 */

import type { Element, ElementContent, Root, RootContent } from 'hast';

/**
 * Property holding the JSON-encoded run. Reaches React as the
 * `data-cite-group` prop.
 */
export const CITE_GROUP_PROPERTY = 'dataCiteGroup';

/** One anchor from a grouped run. */
export type CitationGroupEntry = {
  href: string;
  label: string;
};

/**
 * Parse the `data-cite-group` prop back into entries. Returns undefined when
 * the prop is absent or malformed, so callers fall back to the lone `href`.
 */
export function parseCitationGroup(
  value: unknown
): CitationGroupEntry[] | undefined {
  if (typeof value !== 'string' || value === '') {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return undefined;
  }
  if (!Array.isArray(parsed)) {
    return undefined;
  }
  const entries: CitationGroupEntry[] = [];
  for (const item of parsed) {
    if (typeof item !== 'object' || item === null) {
      return undefined;
    }
    const { href, label } = item as { href?: unknown; label?: unknown };
    if (typeof href !== 'string' || typeof label !== 'string') {
      return undefined;
    }
    entries.push({ href, label });
  }
  return entries.length > 0 ? entries : undefined;
}

function isAnchor(node: ElementContent | RootContent): node is Element {
  return node.type === 'element' && node.tagName === 'a';
}

function isWhitespaceText(node: ElementContent | RootContent): boolean {
  return node.type === 'text' && node.value.trim() === '';
}

/** Concatenated text of an element's direct text children. */
function anchorLabel(node: Element): string {
  return node.children
    .map((child) => (child.type === 'text' ? child.value : ''))
    .join('');
}

function anchorHref(node: Element): string | undefined {
  const href = node.properties?.['href'];
  return typeof href === 'string' && href !== '' ? href : undefined;
}

/**
 * Collapse `run` into its first anchor, annotated with the full run. Anchors
 * whose href was stripped upstream (sanitize/harden) are not citable and are
 * left untouched.
 */
function collapseRun(run: Element[]): ElementContent[] {
  if (run.length === 0) {
    return [];
  }
  const entries: CitationGroupEntry[] = [];
  for (const anchor of run) {
    const href = anchorHref(anchor);
    if (href === undefined) {
      return run;
    }
    entries.push({ href, label: anchorLabel(anchor) });
  }

  const [first] = run;
  if (first === undefined) {
    return run;
  }
  return [
    {
      ...first,
      properties: {
        ...first.properties,
        [CITE_GROUP_PROPERTY]: JSON.stringify(entries),
      },
    },
  ];
}

/**
 * Rewrite one element's children, merging every run of adjacent anchors.
 *
 * Whitespace between anchors is absorbed *speculatively*: it is buffered while
 * a run is open and re-emitted if the next meaningful sibling turns out not to
 * be an anchor. Skipping it eagerly would eat the space in `[a](u) **bold**`.
 */
function groupChildren(children: ElementContent[]): ElementContent[] {
  const out: ElementContent[] = [];
  let run: Element[] = [];
  let pendingWhitespace: ElementContent[] = [];

  const flush = (): void => {
    if (run.length > 0) {
      out.push(...collapseRun(run));
      run = [];
    }
    out.push(...pendingWhitespace);
    pendingWhitespace = [];
  };

  for (const child of children) {
    if (isAnchor(child)) {
      // Whitespace sat between two anchors, so it is interior to the run.
      pendingWhitespace = [];
      run.push(child);
      continue;
    }
    if (run.length > 0 && isWhitespaceText(child)) {
      pendingWhitespace.push(child);
      continue;
    }
    flush();
    out.push(child);
  }
  flush();

  return out;
}

/**
 * Walk every element in the tree. Container-agnostic on purpose — citations
 * land in `p`, `li`, `td`, `th`, `blockquote > p` and headings, and overriding
 * those components individually would mean reimplementing Streamdown's
 * defaults (which are not exported).
 */
function visit(node: Root | Element): void {
  const children = node.children as ElementContent[];
  for (const child of children) {
    if (child.type === 'element') {
      visit(child);
    }
  }
  // Assigned unconditionally: a lone anchor keeps its length but still gains
  // the group property, which is what makes single citations render as chips.
  node.children = groupChildren(children) as typeof node.children;
}

export function rehypeGroupCitations(): (tree: Root) => void {
  return (tree: Root): void => {
    visit(tree);
  };
}
